import { useState, useEffect } from "react";
import { Link, useHistory } from "react-router-dom";
import AOS from "aos";
import "aos/dist/aos.css";
import worldMap from "../../../assets/images/world_map.png";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEnvelope,
  faLock,
  faArrowRight,
} from "@fortawesome/free-solid-svg-icons";
import { useAuth } from "../../../contexts/AuthContext";
import Swal from "sweetalert2";
import { ScrollNavbar } from "../../../components";
import PhoneOtpModal from "../../../components/Auth/PhoneOtpModal";
import { TextField, InputAdornment, IconButton } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";

const Login = () => {
  const history = useHistory();
  const { login } = useAuth();
  const [showOtpModal, setShowOtpModal] = useState(false);

  const redirectAfterLogin = () => {
    const redirectPath = localStorage.getItem("redirect_path");
    if (redirectPath) {
      localStorage.removeItem("redirect_path");

      const redirectStateRaw = localStorage.getItem("redirect_state");
      localStorage.removeItem("redirect_state");
      let redirectState: unknown;
      if (redirectStateRaw) {
        try {
          redirectState = JSON.parse(redirectStateRaw);
        } catch {
          redirectState = undefined;
        }
      }

      history.replace(redirectPath, redirectState);
    } else {
      history.replace("/");
    }
  };

  useEffect(() => {
    AOS.init({
      duration: 1000,
      once: true,
    });
    window.scrollTo(0, 0);
  }, []);

  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);

  const [loading, setLoading] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      await login(formData.email, formData.password);

      Swal.fire("Success", "Logged in successfully!", "success");
      redirectAfterLogin();
    } catch (error: any) {
      Swal.fire("Error", error.message || "An unknown error occurred", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex flex-col bg-gray-50"
      style={{
        backgroundImage: `url(${worldMap})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <ScrollNavbar />
      <main className="flex-grow flex items-center justify-center p-4">
        <div
          className="bg-white/80 backdrop-blur-sm p-8 rounded-2xl shadow-2xl w-full max-w-md border border-gray-200"
          data-aos="fade-up"
        >
          <h2 className="text-3xl font-mazzardbold mb-6 text-center text-gray-800">
            Welcome Back
          </h2>
          <p className="text-center text-gray-600 mb-8">
            Log in to continue to your account.
          </p>
          <form onSubmit={handleSubmit}>
            <TextField
              label="Email address"
              name="email"
              type="email"
              variant="outlined"
              fullWidth
              sx={{ mb: 2.5 }}
              value={formData.email}
              onChange={handleChange}
              required
            />
            <TextField
              label="Password"
              name="password"
              type={showPassword ? "text" : "password"}
              variant="outlined"
              fullWidth
              sx={{ mb: 2.5 }}
              value={formData.password}
              onChange={handleChange}
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-blue-800 to-blue-900 text-white py-3 rounded-lg font-semibold hover:shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center mt-4"
            >
              {loading ? "Logging in..." : "Log in"}
              {!loading && (
                <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
              )}
            </button>
          </form>
          <button
            type="button"
            onClick={() => setShowOtpModal(true)}
            className="w-full mt-3 border border-gray-300 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-50 transition-all duration-300"
          >
            Sign in with phone instead
          </button>
          <p className="mt-6 text-center text-sm text-gray-600">
            Don't have an account?{" "}
            <Link
              to={"/signup"}
              className="text-blue-800 hover:text-blue-900 font-semibold"
            >
              Sign up
            </Link>
          </p>
        </div>
      </main>
      <PhoneOtpModal
        open={showOtpModal}
        onClose={() => setShowOtpModal(false)}
        onVerified={() => {
          setShowOtpModal(false);
          Swal.fire("Success", "Logged in successfully!", "success");
          redirectAfterLogin();
        }}
      />
    </div>
  );
};

export default Login;
